import { apiFetch } from './client';
import type { MetricUnit } from '../components/formatters';

export interface AreaSummary {
  area: string;
  metricCount: number;
}

export interface LayerSummary {
  layer: string;
  areas: AreaSummary[];
}

export interface StructureResponse {
  layers: LayerSummary[];
}

export interface AreaSeriesPoint {
  month: string;
  actual: number | null;
  plan: number | null;
  growthPct: number | null;
}

export interface AreaMetric {
  metric: string;
  subarea: string;
  source: string;
  unit: MetricUnit;
  format: string;
  hasPlan: boolean;
  hasGrowthPct: boolean;
  // True when this source rewrites the whole month on every run, so the in-progress
  // month's actual is a genuine month-to-date figure and can be plotted as one.
  monthToDateActual: boolean;
  series: AreaSeriesPoint[];
}

export interface AreaResponse {
  layer: string;
  area: string;
  months: string[];
  metrics: AreaMetric[];
}

export function fetchStructure(signal?: AbortSignal) {
  return apiFetch<StructureResponse>('/api/structure', { signal });
}

export function fetchArea(layer: string, area: string, signal?: AbortSignal) {
  const qs = new URLSearchParams({ layer, area });
  return apiFetch<AreaResponse>(`/api/areas?${qs.toString()}`, { signal });
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Slugs can lose information (Polish chars, collisions), so resolve them back to
// the canonical Layer/Area names via the structure rather than trusting the URL.
export function resolveArea(
  structure: StructureResponse | undefined,
  layerSlug: string,
  areaSlug: string,
): { layer: string; area: string } | null {
  if (!structure) return null;
  for (const l of structure.layers) {
    if (slugify(l.layer) !== layerSlug) continue;
    for (const a of l.areas) {
      if (slugify(a.area) === areaSlug) return { layer: l.layer, area: a.area };
    }
  }
  return null;
}

export function firstAreaPath(structure: StructureResponse | undefined): string | null {
  const layer = structure?.layers[0];
  const area = layer?.areas[0];
  if (!layer || !area) return null;
  return `/dashboard/${slugify(layer.layer)}/${slugify(area.area)}`;
}
